import { useState, useEffect } from 'react';
import { getPeeths, getJyotirlingas } from '../services/api';
import BackButton from '../components/BackButton';
import { LockIcon } from '../components/SacredIcons';
import { useLang, localizePeeth, localizeJyotirlinga } from '../i18n';

// Leaflet imports — loaded only when MapScreen is mounted
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* Bounding box that contains every Shakti Peeth + Jyotirlinga region:
   Pakistan (Hinglaj, Balochistan) on the west, NE India on the east,
   Sri Lanka (Lankayatri) on the south, Kashmir/Tibet on the north.
   Padded a bit so coastlines have breathing room. */
const PEETH_REGION_BOUNDS = [
  [5.5, 60.0],   // SW: south of Sri Lanka, west of Pakistan
  [37.5, 97.5],  // NE: north of Kashmir, east of Arunachal
];

/* Child component — runs inside <MapContainer> so it can grab the map
   instance via useMap(). Re-fits to the markers whenever they change. */
function FitToMarkers({ peeths, jyotirlingas }) {
  const map = useMap();
  useEffect(() => {
    const coords = [
      ...peeths.filter(p => p.latitude && p.longitude).map(p => [p.latitude, p.longitude]),
      ...jyotirlingas.filter(j => j.latitude && j.longitude).map(j => [j.latitude, j.longitude]),
    ];
    if (coords.length === 0) return;
    map.fitBounds(coords, { padding: [40, 40], maxZoom: 6 });
  }, [peeths, jyotirlingas, map]);
  return null;
}

// Fix leaflet default icon paths broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function makeIcon(emoji, color) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${color};border:2px solid white;
      display:flex;align-items:center;justify-content:center;
      font-size:18px;font-weight:700;color:#fff;
      font-family:'Tiro Devanagari Hindi','Mukta',serif;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${emoji}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  });
}

// Modern padlock glyph (white stroke) for locked map markers — replaces 🔒.
const LOCK_MARKER_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M9 10V7a3.5 3.5 0 0 1 7 0v3" stroke="#fff" stroke-width="1.9" stroke-linecap="round"/>
  <rect x="5.5" y="10" width="13" height="9" rx="2.3" stroke="#fff" stroke-width="1.9"/>
  <circle cx="12" cy="13.6" r="1.35" fill="#fff"/>
  <path d="M12 14.6 L12 16.4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>
</svg>`;

const ICONS = {
  peethUnlocked:      makeIcon('ॐ', '#FF6B00'),
  peethLocked:        makeIcon(LOCK_MARKER_SVG, '#9E9E9E'),
  jyotUnlocked:       makeIcon('⚡', '#D4AF37'),
  jyotLocked:         makeIcon(LOCK_MARKER_SVG, '#B0BEC5'),
};

function formatRam(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + ' लाख';
  if (n >= 1000)   return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

export default function MapScreen({ user, onBack }) {
  const { lang, t } = useLang();
  const [peeths, setPeeths] = useState([]);
  const [jyotirlingas, setJyotirlingas] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const hasLevel5 = (user?.total_ram_count || 0) >= 1165;

  useEffect(() => {
    async function load() {
      try {
        const peethData = await getPeeths();
        setPeeths(peethData.peeths || peethData || []);
      } catch {
        // non-critical
      }

      if (hasLevel5) {
        try {
          const jyotData = await getJyotirlingas();
          setJyotirlingas(jyotData.jyotirlingas || []);
        } catch {
          // non-critical
        }
      }

      setLoading(false);
    }
    load();
  }, [hasLevel5]);

  const journeyPath = [
    ...(filter !== 'jyotirlingas' ? peeths.filter(p => p.is_unlocked && p.latitude && p.longitude).map(p => [p.latitude, p.longitude]) : []),
    ...(filter !== 'peeths' && hasLevel5 ? jyotirlingas.filter(j => j.is_unlocked && j.latitude && j.longitude).map(j => [j.latitude, j.longitude]) : []),
  ];

  const filterBtns = [
    { id: 'all', label: t('map.filterAll') },
    { id: 'peeths', label: t('map.filterPeeths') },
    ...(hasLevel5 ? [{ id: 'jyotirlingas', label: t('map.filterJyoti') }] : []),
  ];

  const localizedPeeths = peeths.map(p => localizePeeth(p, lang));
  const localizedJyot   = jyotirlingas.map(j => localizeJyotirlinga(j, lang));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: 'var(--bg-primary, #FFF8F0)' }}>
      {/* Header */}
      <div className="screen-header" style={{ flexShrink: 0 }}>
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <div className="screen-header-title">{t('map.title')}</div>
          <div className="screen-header-sub">{t('map.subtitle')}</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', background: 'var(--bg-secondary, #FFF3E0)', flexShrink: 0, borderBottom: '1px solid rgba(201,130,26,0.15)' }}>
        {filterBtns.map(btn => (
          <button
            key={btn.id}
            onClick={() => setFilter(btn.id)}
            style={{
              padding: '6px 14px', borderRadius: 20,
              border: filter === btn.id ? '1.5px solid rgba(201,130,26,0.55)' : '1px solid rgba(201,130,26,0.18)',
              background: filter === btn.id ? 'linear-gradient(135deg, rgba(230,81,0,0.18), rgba(201,130,26,0.25))' : 'rgba(255,248,232,0.5)',
              color: filter === btn.id ? 'var(--saffron-dark, #BF360C)' : 'var(--text-soft, #9B4400)',
              fontFamily: 'var(--font-hindi)', fontSize: 13,
              cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Map */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div className="spinner" />
          <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 18, color: 'var(--text-dark)' }}>{t('map.loading')}</span>
        </div>
      ) : (
        <MapContainer
          center={[22.5, 80.0]}
          zoom={5}
          minZoom={4}
          maxZoom={9}
          maxBounds={PEETH_REGION_BOUNDS}
          maxBoundsViscosity={1.0}
          style={{ flex: 1, width: '100%' }}
          zoomControl={true}
          worldCopyJump={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            bounds={PEETH_REGION_BOUNDS}
            noWrap={true}
          />

          {/* Auto-fit to all marker positions when data loads */}
          <FitToMarkers peeths={peeths} jyotirlingas={jyotirlingas} />

          {/* Journey path */}
          {journeyPath.length > 1 && (
            <Polyline
              positions={journeyPath}
              color="#FF6B00"
              weight={2}
              opacity={0.65}
              dashArray="6 4"
            />
          )}

          {/* Shakti Peeth markers */}
          {(filter === 'all' || filter === 'peeths') && localizedPeeths.map(p => (
            p.latitude && p.longitude ? (
              <Marker
                key={`peeth-${p.peeth_id}`}
                position={[p.latitude, p.longitude]}
                icon={p.is_unlocked ? ICONS.peethUnlocked : ICONS.peethLocked}
              >
                <Popup>
                  <div style={{ fontFamily: 'var(--font-hindi, sans-serif)', minWidth: 160 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#3A1A00', marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#7A4020' }}>📍 {p.location}, {p.state}</div>
                    {p.shakti_name && <div style={{ fontSize: 12, color: '#7A4020', marginTop: 2 }}>🔱 {p.shakti_name}</div>}
                    <div style={{ marginTop: 6, display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: p.is_unlocked ? 'rgba(76,175,80,0.12)' : 'rgba(150,150,150,0.10)',
                      color: p.is_unlocked ? '#2E7D32' : '#666',
                      border: `1px solid ${p.is_unlocked ? 'rgba(76,175,80,0.3)' : 'rgba(150,150,150,0.2)'}`,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <LockIcon size={12} open={p.is_unlocked} color={p.is_unlocked ? '#2E7D32' : '#666'} />
                        {p.is_unlocked ? t('map.popupUnlocked') : t('map.popupLockedRam', { ram: formatRam(p.ram_count_to_unlock) })}
                      </span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ) : null
          ))}

          {/* Jyotirlinga markers */}
          {(filter === 'all' || filter === 'jyotirlingas') && localizedJyot.map(j => (
            j.latitude && j.longitude ? (
              <Marker
                key={`jyot-${j.jyotirlinga_id}`}
                position={[j.latitude, j.longitude]}
                icon={j.is_unlocked ? ICONS.jyotUnlocked : ICONS.jyotLocked}
              >
                <Popup>
                  <div style={{ fontFamily: 'var(--font-hindi, sans-serif)', minWidth: 160 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#3A1A00', marginBottom: 4 }}>{j.name}</div>
                    <div style={{ fontSize: 12, color: '#7A4020' }}>📍 {j.location}, {j.state}</div>
                    {j.shiva_form && <div style={{ fontSize: 12, color: '#7A4020', marginTop: 2 }}>⚡ {j.shiva_form}</div>}
                    <div style={{ marginTop: 6, display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: j.is_unlocked ? 'rgba(76,175,80,0.12)' : 'rgba(150,150,150,0.10)',
                      color: j.is_unlocked ? '#2E7D32' : '#666',
                      border: `1px solid ${j.is_unlocked ? 'rgba(76,175,80,0.3)' : 'rgba(150,150,150,0.2)'}`,
                    }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <LockIcon size={12} open={j.is_unlocked} color={j.is_unlocked ? '#2E7D32' : '#666'} />
                        {j.is_unlocked ? t('map.popupUnlocked') : t('map.popupLockedRam', { ram: formatRam(j.ram_count_to_unlock) })}
                      </span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ) : null
          ))}
        </MapContainer>
      )}

      {/* Legend */}
      {!loading && (
        <div style={{
          display: 'flex', gap: 12, padding: '8px 14px',
          background: 'rgba(255,248,232,0.92)', borderTop: '1px solid rgba(201,130,26,0.18)',
          flexShrink: 0, overflowX: 'auto',
        }}>
          {[
            { color: '#FF6B00', label: t('map.legendPeethOpen') },
            { color: '#9E9E9E', label: t('map.legendPeethLocked') },
            ...(hasLevel5 ? [
              { color: '#D4AF37', label: t('map.legendJyotOpen') },
              { color: '#B0BEC5', label: t('map.legendJyotLocked') },
            ] : []),
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 11, color: 'var(--text-mid)' }}>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
